import { EnvironmentProviders, provideZonelessChangeDetection } from '@angular/core';

/**
 * The application's change-detection strategy. FTK-7588 P4: the account app is ZONELESS.
 *
 * Angular 21's own default is zoneless (`NoopNgZone`); this app had opted back into zone-based CD
 * to preserve pre-21 behaviour, and the burn-down existed to pay that opt-in off. It is paid: the
 * `zone.js` polyfill and the dependency are gone, and this file no longer has a twin to be swapped
 * for.
 *
 * ── What was here before, and why it is not any more ──────────────────────────────────────────
 *
 * P3 gave this file a `change-detection.zoneless.config.ts` twin and angular.json a `zoneless`
 * configuration, so a zoneless build could be RUN and gated before it was the default. Both are
 * deleted with the flip: the default build is now the zoneless build, so a configuration that
 * swaps one for the other has nothing left to express.
 *
 * 🚨 THE GATE THAT PROVED THIS DID NOT GO WITH THEM, AND IT INVERTED. `provideZonelessChangeDetection()`
 * warns NG0914 whenever `Zone` is defined in dev mode. Under P3 the zoneless build always warned —
 * zone.js was deliberately still on polyfills.ts — and that warning was the only thing that could
 * tell a real zoneless run from the same command against the wrong bundle. Now the shipped app is
 * zoneless AND warning-free, so NG0914's ABSENCE is the evidence the polyfill actually left rather
 * than merely stopping being imported. `assertChangeDetectionMode` in
 * `account-parity/specs/cd-staleness.spec.ts` now asserts exactly that, on every route.
 *
 * Do not silence or delete that assertion. An app that starts logging NG0914 again has had zone.js
 * reintroduced — by a dependency pulling it in, or by someone restoring the polyfill import — and
 * that is the one P4 regression no bundle report would flag as an error.
 *
 * ── 🚨 THE PROVIDER AND THE POLYFILL ARE NOW COUPLED IN BOTH DIRECTIONS ───────────────────────
 *
 * There is no `src/polyfills.ts` any more — angular.json names `@angular/localize/init` directly,
 * the way every other app in this repo does — so this file is the only place left that decides
 * whether the app is zoneless. Both ways of half-undoing P4 are traps, and they fail very
 * differently:
 *
 *   - REINSTATING zone.js and leaving this file alone does NOT restore zone-based change
 *     detection. The provider decides that, and every `zoneIsDefined` branch in Angular's
 *     scheduler is gated on `!zonelessEnabled` first. All you get is every global timer, event
 *     and XHR re-patched for nothing, ~12.5 KB gzip back in the eager set, and NG0914 on every
 *     route — which the parity gate fails on, deliberately.
 *   - REVERTING THIS FILE to `provideZoneChangeDetection()` without also reinstating zone.js does
 *     not degrade, it does not boot. `NgZone`'s constructor throws NG0908 ("In this configuration
 *     Angular requires Zone.js") when `Zone` is undefined, and in a production bundle the message
 *     is stripped to the bare code.
 *
 * So a genuine revert of P4 is BOTH edits together, and neither alone is a smaller version of it.
 *
 * ── If you are debugging a view that will not repaint ─────────────────────────────────────────
 *
 * Under zones every tick was GLOBAL, so any notification anywhere repainted everything Default.
 * That is gone. A write now has to notify the view that RENDERS it:
 *
 *   - `markForCheck()` marks the CALLING view AND EVERY ANCESTOR UP TO THE ROOT, and the ancestors
 *     are RE-EVALUATED, not merely descended through. `markViewDirty` ORs `RefreshView | Dirty`
 *     into each view in the chain, and `detectChangesInView` does
 *     `shouldRefreshView ||= !!(flags & RefreshView)`, so `refreshView` runs their templates in
 *     update mode. A write into an object a PARENT renders therefore IS fixed by marking the
 *     child. Pinned as case 4 of `src/app/common/onpush-ancestor-refresh.spec.ts`.
 *     🚨 "ANCESTOR" MEANS THE DECLARING VIEW CHAIN, NOT THE DOM NESTING, and for this app's
 *     editors those differ. `markViewDirty` walks `lView[PARENT]`, and a component passed as
 *     PROJECTED CONTENT was CREATED by the view that wrote its tag — so the host it visually
 *     sits inside is not in the chain and is NOT re-evaluated. `<ft-modal-editor-wrapper>`
 *     renders `<ng-content />`, so marking a projected child does NOT repaint the wrapper,
 *     and there the `@Output` hop the old text reached for really is the answer. MEASURED as
 *     case 5 of the same spec, against case 4's identical-but-declared arm.
 *     🚨 This paragraph used to say the opposite — "ancestors only for TRAVERSAL … not fixed by
 *     marking the child … route it through an `@Output` or take an `ApplicationRef.tick()`" —
 *     which sent readers to a hop or a whole app-wide tick for a case one `markForCheck()`
 *     already covers. It was measured false against the installed @angular/core 21.2.19.
 *     The narrow caveat that survives: a mark raised DURING a check takes the
 *     `isRefreshingViews()` branch, which ORs `Dirty` ALONE, so an ancestor the pass has already
 *     walked past waits for the next tick.
 *   - a `signal` write notifies on its own; an IN-PLACE mutation of a signal's value does not.
 *   - `HttpClient` opens a PendingTask whose removal notifies the scheduler, so ordinary request
 *     -> assign -> render paths are already covered.
 *   - a lazy `import()` opens NO PendingTask. An async continuation behind one notifies nothing.
 *
 * `flipto/apps/account/scripts/cd-onpush-audit.mjs --zoneless --ratchet` is the detector for the
 * common shape, and `flipto/apps/account/ZONELESS-PLAN.md` is the full record.
 */
export const changeDetectionProviders: EnvironmentProviders[] = [provideZonelessChangeDetection()];
